import React, { useEffect, useState, useMemo } from 'react';
import { getStaff, createStaff, updateStaff, deleteStaff, toggleStaffActive, resetStaffPassword, getBillingEntities } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { exportToExcel, fmtDate as fmtDateExp, exportAmount } from '../utils/exportExcel';
import { formatDateDdMmmYy } from '../utils/format';

interface Staff {
  id: number;
  staffName: string;
  isPartner: boolean;
  perHourCost: number;
  email: string;
  phone?: string | null;
  role?: string;
  isActive: boolean;
  reportingPartner?: { id: number; staffName: string } | null;
  reportingPartnerId?: number | null;
  dateOfBirth?: string | null;
  joiningDate?: string | null;
  exitDate?: string | null;
  isFieldStaff?: boolean;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  bankBeneficiaryName?: string | null;
  paymentEntityId?: number | null;
}

const defaultForm = {
  staffName: '', isPartner: false, perHourCost: '', email: '', phone: '', password: '',
  role: 'STAFF', reportingPartnerId: '', dateOfBirth: '', joiningDate: '', exitDate: '',
  isFieldStaff: false,
  bankAccountNumber: '', bankIfsc: '', bankBeneficiaryName: '', paymentEntityId: '',
};

const roleBadge = (role?: string) => {
  if (role === 'ADMIN') return 'bg-purple-100 text-purple-700';
  if (role === 'IT') return 'bg-indigo-100 text-indigo-700';
  if (role === 'HR') return 'bg-pink-100 text-pink-700';
  return 'bg-gray-100 text-gray-600';
};

/**
 * A labelled band in the staff form.
 *
 * The form had grown to fourteen stacked fields, which made it taller than the
 * viewport on a laptop. Grouping them under an icon and heading lets the fields
 * sit two-per-row without the reader losing track of what each one belongs to,
 * which is what actually shortens the form.
 */
const FormSection: React.FC<{ icon: string; title: string; hint?: string; children: React.ReactNode }> = ({
  icon, title, hint, children,
}) => (
  <section>
    <div className="flex items-center gap-2 mb-3">
      <span aria-hidden className="text-base leading-none">{icon}</span>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h4>
      {hint && <span className="text-xs text-gray-400">· {hint}</span>}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">{children}</div>
  </section>
);

type StaffSortField = 'staffName' | 'email' | 'role' | 'isActive';

const Admin: React.FC<{ openStaffId?: number }> = ({ openStaffId }) => {
  const { isAdmin, isHR } = useAuth();
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Staff | null>(null);
  const [form, setForm] = useState(defaultForm);
  const [error, setError] = useState('');
  // Guards the staff create/edit submit against a fast repeat click — see
  // Tasks.tsx certSaving/taskSaving for the same pattern.
  const [staffSaving, setStaffSaving] = useState(false);
  const [entities, setEntities] = useState<{ id: number; name: string }[]>([]);

  // Filter + sort
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortField, setSortField] = useState<StaffSortField>('staffName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Password reset
  const [resetTarget, setResetTarget] = useState<Staff | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetMsg, setResetMsg] = useState('');

  useEffect(() => { fetchStaff(); }, []);
  useEffect(() => {
    if (isAdmin || isHR) getBillingEntities().then((r) => setEntities(r.data)).catch(() => {});
  }, [isAdmin, isHR]);

  useEffect(() => {
    if (openStaffId && staff.length > 0) {
      const s = staff.find((x) => x.id === openStaffId);
      if (s) openEdit(s);
    }
  }, [openStaffId, staff]); // openEdit is stable (no deps change)

  const fetchStaff = async () => {
    try {
      const res = await getStaff();
      setStaff(res.data);
    } finally {
      setLoading(false);
    }
  };

  const partners = staff.filter((s) => s.isPartner && s.isActive);

  const handleSort = (f: StaffSortField) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
  };
  const SortIcon = ({ f }: { f: StaffSortField }) =>
    sortField !== f ? <span className="ml-1 text-gray-300">↕</span>
    : <span className="ml-1 text-blue-600">{sortDir === 'asc' ? '↑' : '↓'}</span>;

  const filteredStaff = useMemo(() => {
    let list = staff;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        s.staffName.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        (s.phone || '').includes(q)
      );
    }
    if (filterRole) list = list.filter(s => (s.role || 'STAFF') === filterRole);
    if (filterStatus) list = list.filter(s => filterStatus === 'active' ? s.isActive : !s.isActive);
    return [...list].sort((a, b) => {
      let av = '', bv = '';
      if (sortField === 'staffName') { av = a.staffName; bv = b.staffName; }
      else if (sortField === 'email') { av = a.email; bv = b.email; }
      else if (sortField === 'role') { av = a.role || 'STAFF'; bv = b.role || 'STAFF'; }
      else if (sortField === 'isActive') { av = a.isActive ? '0' : '1'; bv = b.isActive ? '0' : '1'; }
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [staff, search, filterRole, filterStatus, sortField, sortDir]);

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (s: Staff) => {
    setEditing(s);
    setForm({
      staffName: s.staffName,
      isPartner: s.isPartner,
      isFieldStaff: s.isFieldStaff ?? false,
      perHourCost: String(s.perHourCost),
      email: s.email,
      phone: s.phone || '',
      password: '',
      role: s.role || 'STAFF',
      reportingPartnerId: s.reportingPartnerId ? String(s.reportingPartnerId) : '',
      dateOfBirth: s.dateOfBirth ? s.dateOfBirth.slice(0, 10) : '',
      joiningDate: s.joiningDate ? s.joiningDate.slice(0, 10) : '',
      exitDate: s.exitDate ? s.exitDate.slice(0, 10) : '',
      bankAccountNumber: s.bankAccountNumber || '',
      bankIfsc: s.bankIfsc || '',
      bankBeneficiaryName: s.bankBeneficiaryName || '',
      paymentEntityId: s.paymentEntityId ? String(s.paymentEntityId) : '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (staffSaving) return;
    setError('');
    const payload: any = {
      staffName: form.staffName,
      email: form.email,
      phone: form.phone || null,
      password: form.password,
      reportingPartnerId: form.reportingPartnerId || null,
      dateOfBirth: form.dateOfBirth || null,
      joiningDate: form.joiningDate || null,
      exitDate: form.exitDate || null,
      bankAccountNumber: form.bankAccountNumber,
      bankIfsc: form.bankIfsc,
      bankBeneficiaryName: form.bankBeneficiaryName,
      paymentEntityId: form.paymentEntityId || null,
    };
    // Attendance-only labour holds no role beyond punching in and never
    // reaches a payroll run, so the two fields the form stops showing are
    // pinned rather than left at whatever the record happened to carry. Read
    // from the record's own flag, not from isAdmin: HR can edit an existing
    // field-staff member (they just cannot set the flag), and their save has
    // to land on the same values. The server clamps these too.
    const fieldStaff = form.isFieldStaff;
    if (isAdmin || isHR) {
      payload.isPartner = form.isPartner;
      payload.perHourCost = fieldStaff ? 0 : Number(form.perHourCost);
    }
    if (isAdmin) {
      payload.role = fieldStaff ? 'STAFF' : form.role;
      payload.isFieldStaff = form.isFieldStaff;
    } else {
      // HR: can set IT or STAFF role only
      payload.role = fieldStaff ? 'STAFF' : (form.role === 'IT' ? 'IT' : 'STAFF');
    }
    setStaffSaving(true);
    try {
      if (editing) {
        await updateStaff(editing.id, payload);
      } else {
        await createStaff(payload);
      }
      setShowModal(false);
      fetchStaff();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving staff');
    } finally {
      setStaffSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this staff member? This cannot be undone.')) return;
    try {
      await deleteStaff(id);
      fetchStaff();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Cannot delete staff');
    }
  };

  const handleToggleActive = async (id: number, name: string, currentlyActive: boolean) => {
    const action = currentlyActive ? 'disable' : 'enable';
    if (!window.confirm(`${action === 'disable' ? 'Disable' : 'Enable'} ${name}?`)) return;
    try {
      await toggleStaffActive(id);
      fetchStaff();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error updating staff');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Staff Admin</h2>
          <p className="text-gray-500 text-sm mt-1">Manage staff members and their access</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary text-sm"
            onClick={() => exportToExcel(
              filteredStaff.map((s) => ({
                ID: s.id,
                Name: s.staffName,
                Email: s.email,
                Phone: s.phone || '',
                Role: s.role || 'STAFF',
                'Is Partner': s.isPartner ? 'Yes' : 'No',
                'Per Hour Cost': exportAmount(s.perHourCost),
                'Reporting Partner': s.reportingPartner?.staffName || '',
                'Date of Birth': fmtDateExp(s.dateOfBirth),
                'Joining Date': fmtDateExp(s.joiningDate),
                Status: s.isActive ? 'Active' : 'Disabled',
              })),
              'Staff',
              'Staff',
            )}
          >
            ⬇ Export Excel
          </button>
          <button className="btn-primary" onClick={openCreate}>+ Add Staff</button>
        </div>
      </div>

      <div className="card">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <input className="input-field max-w-xs" placeholder="Search name, email, phone…" value={search}
            onChange={e => setSearch(e.target.value)} />
          <select className="input-field w-36" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
            <option value="">All Roles</option>
            <option value="ADMIN">Admin</option>
            <option value="HR">HR</option>
            <option value="IT">IT</option>
            <option value="STAFF">Staff</option>
          </select>
          <select className="input-field w-36" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Disabled</option>
          </select>
          <span className="text-sm text-gray-400 self-center">{filteredStaff.length} staff</span>
        </div>
        {loading ? (
          <p className="text-gray-500 text-sm">Loading...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="table-header cursor-pointer select-none" onClick={() => handleSort('staffName')}>
                    Name <SortIcon f="staffName" />
                  </th>
                  <th className="table-header cursor-pointer select-none" onClick={() => handleSort('email')}>
                    Email / Phone <SortIcon f="email" />
                  </th>
                  <th className="table-header cursor-pointer select-none" onClick={() => handleSort('role')}>
                    Role <SortIcon f="role" />
                  </th>
                  <th className="table-header">Partner</th>
                  <th className="table-header">Reporting To</th>
                  {(isAdmin || isHR) && <th className="table-header">Rate/hr</th>}
                  <th className="table-header cursor-pointer select-none" onClick={() => handleSort('isActive')}>
                    Status <SortIcon f="isActive" />
                  </th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.map((s) => (
                  <tr key={s.id} className={`hover:bg-gray-50 ${!s.isActive ? 'opacity-50 bg-gray-50' : ''}`}>
                    <td className="table-cell">
                      <div className="font-medium">{s.staffName}</div>
                      {s.dateOfBirth && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          🎂 {formatDateDdMmmYy(s.dateOfBirth)}
                        </div>
                      )}
                      {s.joiningDate && (
                        <div className="text-xs text-gray-400">
                          📅 Joined {formatDateDdMmmYy(s.joiningDate)}
                        </div>
                      )}
                    </td>
                    <td className="table-cell">
                      <div className="text-gray-500">{s.email}</div>
                      {s.phone && <div className="text-xs text-gray-400 mt-0.5">📞 {s.phone}</div>}
                    </td>
                    <td className="table-cell">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${roleBadge(s.role)}`}>
                        {s.role || 'STAFF'}
                      </span>
                    </td>
                    <td className="table-cell">
                      <span className={s.isPartner ? 'text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium' : 'text-gray-400 text-xs'}>
                        {s.isPartner ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="table-cell text-gray-500 text-xs">
                      {s.reportingPartner?.staffName || '—'}
                    </td>
                    {(isAdmin || isHR) && (
                      <td className="table-cell">₹{Number(s.perHourCost).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                    )}
                    <td className="table-cell">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {s.isActive ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex gap-2 flex-wrap">
                        {(isAdmin || isHR) && (
                          <button className="text-blue-600 hover:text-blue-800 text-xs font-medium" onClick={() => openEdit(s)}>
                            Edit
                          </button>
                        )}
                        <button
                          className={`text-xs font-medium ${s.isActive ? 'text-orange-600 hover:text-orange-800' : 'text-green-600 hover:text-green-800'}`}
                          onClick={() => handleToggleActive(s.id, s.staffName, s.isActive)}
                        >
                          {s.isActive ? 'Disable' : 'Enable'}
                        </button>
                        {isAdmin && (
                          <button className="text-red-600 hover:text-red-800 text-xs font-medium" onClick={() => handleDelete(s.id)}>
                            Delete
                          </button>
                        )}
                        {(isAdmin || isHR) && (
                          <button className="text-purple-600 hover:text-purple-800 text-xs font-medium"
                            onClick={() => { setResetTarget(s); setNewPassword(''); setResetMsg(''); }}>
                            Reset Pwd
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredStaff.length === 0 && (
                  <tr>
                    <td colSpan={8} className="table-cell text-center text-gray-400 py-8">No staff members found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          {/* max-h plus a flex column: the field area scrolls while the heading
              and the action buttons stay put. Before this the panel simply grew
              past the bottom of the screen, with no scrollbar anywhere, and on
              a laptop the Update button could not be reached at all. */}
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {editing ? 'Edit Staff Member' : 'Add Staff Member'}
                </h3>
                {form.isFieldStaff && (
                  <p className="text-xs text-amber-700 mt-1">
                    Attendance-only record — saves as role <strong>Staff</strong> with an hourly cost of
                    <strong> ₹0</strong>, and reporting fields are not asked for.
                  </p>
                )}
              </div>
              <button type="button" onClick={() => setShowModal(false)} aria-label="Close"
                className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1">×</button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">

                <FormSection icon="👤" title="Identity">
                  <div>
                    <label className="label">Staff Name</label>
                    <input className="input-field" value={form.staffName}
                      onChange={(e) => setForm({ ...form, staffName: e.target.value })} required />
                  </div>
                  <div>
                    <label className="label">Email</label>
                    <input type="email" className="input-field" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                  </div>
                  <div>
                    <label className="label">Phone</label>
                    <input type="tel" className="input-field" value={form.phone} placeholder="10-digit mobile number"
                      onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  {!editing && (
                    <div>
                      <label className="label">Password</label>
                      <input type="password" className="input-field" value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        placeholder="Blank for Welcome@123" />
                    </div>
                  )}
                </FormSection>

                {isAdmin && (
                  <label htmlFor="isFieldStaff"
                    className={`block rounded-lg border p-3 cursor-pointer transition-colors ${
                      form.isFieldStaff
                        ? 'border-amber-300 bg-amber-50'
                        : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                    }`}>
                    <div className="flex items-start gap-2.5">
                      <input type="checkbox" id="isFieldStaff" className="w-4 h-4 mt-0.5 text-blue-600 rounded"
                        checked={form.isFieldStaff}
                        onChange={(e) => setForm({ ...form, isFieldStaff: e.target.checked })} />
                      <div>
                        <span className="text-sm font-medium text-gray-700">🚜 Field staff — attendance only</span>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Farm and site labour: punch screen only, several punches a day, hidden from
                          everyone but Admin and HR, left out of payroll runs, phone stays signed in.
                        </p>
                      </div>
                    </div>
                  </label>
                )}

                {/* Hidden for attendance-only labour: they hold no role beyond
                    punching in, are excluded from payroll runs, and sit outside
                    the approval chain a reporting partner exists for. Values
                    already on the record are left alone rather than rewritten —
                    ticking the box asks fewer questions, it does not silently
                    edit someone's pay rate or demote them. */}
                {!form.isFieldStaff && (
                  <FormSection icon="🔑" title="Role & Billing">
                    <div>
                      <label className="label">Role</label>
                      <select className="input-field" value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}>
                        <option value="STAFF">Staff</option>
                        <option value="IT">IT (can resolve tickets)</option>
                        {isAdmin && <option value="HR">HR (reviews reimbursements)</option>}
                        {isAdmin && <option value="ADMIN">Admin (full access)</option>}
                      </select>
                    </div>
                    {(isAdmin || isHR) && (
                      <div>
                        <label className="label">Per Hour Cost (₹)</label>
                        <input type="number" className="input-field" value={form.perHourCost} min="0"
                          onChange={(e) => setForm({ ...form, perHourCost: e.target.value })} required />
                      </div>
                    )}
                    <div>
                      <label className="label">Reporting Partner</label>
                      <select className="input-field" value={form.reportingPartnerId}
                        onChange={(e) => setForm({ ...form, reportingPartnerId: e.target.value })}>
                        <option value="">— None —</option>
                        {partners.map((p) => (
                          <option key={p.id} value={p.id}>{p.staffName}</option>
                        ))}
                      </select>
                    </div>
                    {(isAdmin || isHR) && (
                      <div className="flex items-center gap-2 sm:pt-7">
                        <input type="checkbox" id="isPartner" className="w-4 h-4 text-blue-600 rounded"
                          checked={form.isPartner}
                          onChange={(e) => setForm({ ...form, isPartner: e.target.checked })} />
                        <label htmlFor="isPartner" className="text-sm font-medium text-gray-700">Is Partner</label>
                      </div>
                    )}
                  </FormSection>
                )}

                {(isAdmin || isHR) && (
                  <FormSection icon="🏦" title="Bank & Payments" hint="used for salary, reimbursement and other payments">
                    <div>
                      <label className="label">Paid from</label>
                      <select className="input-field" value={form.paymentEntityId}
                        onChange={(e) => setForm({ ...form, paymentEntityId: e.target.value })}>
                        <option value="">— Select entity —</option>
                        {entities.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label">Beneficiary name</label>
                      <input className="input-field" value={form.bankBeneficiaryName} placeholder={form.staffName || 'As per bank account'}
                        onChange={(e) => setForm({ ...form, bankBeneficiaryName: e.target.value })} />
                      <p className="text-xs text-gray-400 mt-1">Leave blank to use the staff name.</p>
                    </div>
                    <div>
                      <label className="label">Bank account number</label>
                      <input className="input-field" inputMode="numeric" value={form.bankAccountNumber}
                        onChange={(e) => setForm({ ...form, bankAccountNumber: e.target.value })} />
                    </div>
                    <div>
                      <label className="label">IFSC</label>
                      <input className="input-field uppercase" maxLength={11} value={form.bankIfsc}
                        onChange={(e) => setForm({ ...form, bankIfsc: e.target.value.toUpperCase() })} />
                    </div>
                  </FormSection>
                )}

                <FormSection icon="📅" title="Dates">
                  <div>
                    <label className="label">Date of Birth</label>
                    <input type="date" className="input-field" value={form.dateOfBirth}
                      onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Joining Date</label>
                    <input type="date" className="input-field" value={form.joiningDate}
                      onChange={(e) => setForm({ ...form, joiningDate: e.target.value })} />
                  </div>
                  {editing && (isAdmin || isHR) && (
                    <div>
                      <label className="label">Exit Date</label>
                      <input type="date" className="input-field" value={form.exitDate}
                        onChange={(e) => setForm({ ...form, exitDate: e.target.value })} />
                      <p className="text-xs text-gray-400 mt-1">
                        Blank while active. Prorates the final month's payroll.
                      </p>
                    </div>
                  )}
                </FormSection>
              </div>

              <div className="border-t border-gray-200 bg-gray-50 rounded-b-xl px-6 py-4 flex items-center gap-3">
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <div className="ml-auto flex gap-3">
                  <button type="button" className="btn-secondary" disabled={staffSaving}
                    onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={staffSaving}>
                    {staffSaving ? 'Saving…' : editing ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Reset Password Modal ── */}
      {resetTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Reset Password</h3>
            <p className="text-sm text-gray-500 mb-4">Set a new password for <span className="font-medium">{resetTarget.staffName}</span></p>
            <input
              type="password"
              className="input-field w-full mb-3"
              placeholder="New password (min 6 chars)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            {resetMsg && <p className={`text-sm mb-3 ${resetMsg.startsWith('✓') ? 'text-green-600' : 'text-red-600'}`}>{resetMsg}</p>}
            <div className="flex gap-3 justify-end">
              <button className="btn-secondary" onClick={() => setResetTarget(null)}>Cancel</button>
              <button className="btn-primary" onClick={async () => {
                if (newPassword.length < 6) { setResetMsg('Password must be at least 6 characters'); return; }
                try {
                  await resetStaffPassword(resetTarget.id, newPassword);
                  setResetMsg('✓ Password reset successfully');
                  setTimeout(() => setResetTarget(null), 1500);
                } catch {
                  setResetMsg('Failed to reset password');
                }
              }}>Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
